import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import css from '@styled-system/css';
import shouldForwardProp from '@styled-system/should-forward-prop';

import sx from '../../internal/styles/sx';
import { tokenizedCss } from '../../internal/styles/tokenizedCss';
import { tokenizedVariant } from '../../internal/styles/tokenizedVariant';
import { LinkProps } from './types';

const BaseLink = styled('a', {
  shouldForwardProp: (prop) =>
    shouldForwardProp(prop) && !['mode', 'variant', 'size'].includes(prop),
})<LinkProps>(
  css({
    fontFamily: 'body',
    cursor: 'pointer',
    textDecoration: 'underline',
  }),
  tokenizedCss({ scale: 'link' }),
  tokenizedVariant({ scale: 'link', prop: 'variant' }),
  tokenizedVariant({ scale: 'link', prop: 'mode' }),
  sx
);

const DefaultLink = styled(BaseLink)<LinkProps>(({ size = 'base' }) =>
  css({
    fontSize: `bodies.${size}`,
    textDecorationStyle: 'dotted',
    textUnderlineOffset: '4px',
  })
);

const InlineLink = styled(BaseLink)<LinkProps>(({ size = 'base' }) =>
  css({
    fontSize: `bodies.${size}`,
  })
);

const LegalLink = styled(BaseLink)<LinkProps>(
  css({
    fontSize: 'bodies.xSmall',
  })
);

const components = {
  default: DefaultLink,
  legal: LegalLink,
  inline: InlineLink,
};

const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ variant = 'default', mode = 'default', onClick, ...rest }, ref) => {
    const LinkComponent = components[variant];
    const handleClick = (event) => {
      if (onClick) {
        event.preventDefault();
        onClick(event);
      }
    };

    return (
      <LinkComponent
        mode={mode}
        variant={variant}
        onClick={handleClick}
        ref={ref}
        {...rest}
      />
    );
  }
);

export default Link;
